"use client";

/**
 * `<ProductCard>` — interactive card for the ThreeProducts homepage
 * section. Client-only because hover state lifts the card and
 * brightens the shadow / border on the flagship variant.
 */

import { memo, useState, type ReactNode } from "react";
import Link from "next/link";
import { ArrowR } from "../icons";

interface ProductCardProps {
  name: string;
  tag: string;
  icon: ReactNode;
  desc: string;
  price: string;
  priceSuffix: string;
  meta: string;
  flagship?: boolean;
  href: string;
}

function ProductCardImpl({
  name,
  tag,
  icon,
  desc,
  price,
  priceSuffix,
  meta,
  flagship,
  href,
}: ProductCardProps) {
  const [hover, setHover] = useState(false);

  return (
    <Link
      href={href}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        background: flagship
          ? "linear-gradient(180deg, var(--color-surface-2) 0%, var(--color-surface) 100%)"
          : "var(--color-surface)",
        border: `1px solid ${flagship ? "rgba(59,130,246,0.25)" : "var(--color-line)"}`,
        borderRadius: 14,
        padding: 32,
        height: "100%",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.16,1,0.3,1)",
        transform: hover ? "translateY(-4px)" : "none",
        boxShadow: flagship
          ? hover
            ? "0 20px 50px -20px rgba(59,130,246,0.30), 0 0 0 1px rgba(59,130,246,0.20)"
            : "0 8px 30px -10px rgba(0,0,0,0.4)"
          : hover
            ? "0 16px 40px -16px rgba(0,0,0,0.5)"
            : "none",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {flagship ? (
        <div
          style={{
            position: "absolute",
            top: 22,
            right: 22,
            fontSize: 9,
            padding: "4px 8px",
            borderRadius: 4,
            background: "rgba(59,130,246,0.12)",
            color: "var(--color-blue-2)",
            letterSpacing: "0.14em",
            fontWeight: 600,
            fontFamily: "var(--font-mono)",
          }}
        >
          FLAGSHIP
        </div>
      ) : null}

      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 10,
          background: flagship
            ? "rgba(59,130,246,0.10)"
            : "rgba(255,255,255,0.03)",
          border: `1px solid ${flagship ? "rgba(59,130,246,0.20)" : "var(--color-line)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 26,
          color: flagship ? "var(--color-blue-2)" : "var(--color-muted)",
        }}
      >
        {icon}
      </div>

      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--color-dim)",
          letterSpacing: "0.14em",
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        {meta}
      </div>
      <h3
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: "var(--color-text)",
          margin: 0,
          letterSpacing: "-0.015em",
        }}
      >
        {name}
      </h3>
      <div
        style={{
          fontSize: 14,
          color: flagship ? "var(--color-blue-2)" : "var(--color-muted)",
          marginTop: 4,
          fontWeight: flagship ? 500 : 400,
        }}
      >
        {tag}
      </div>

      <p
        style={{
          fontSize: 14,
          color: "var(--color-muted)",
          lineHeight: 1.6,
          marginTop: 20,
          marginBottom: 28,
          flex: 1,
        }}
      >
        {desc}
      </p>

      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 6,
          marginBottom: 22,
        }}
      >
        <span
          style={{
            fontSize: 32,
            fontWeight: 600,
            color: "var(--color-text)",
            letterSpacing: "-0.02em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {price}
        </span>
        <span style={{ fontSize: 13, color: "var(--color-dim)" }}>
          {priceSuffix}
        </span>
      </div>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          color: flagship ? "var(--color-blue-2)" : "var(--color-muted)",
          fontSize: 14,
          fontWeight: 500,
        }}
      >
        Learn more
        <span
          style={{
            display: "inline-flex",
            transform: hover ? "translateX(4px)" : "none",
            transition: "transform 0.2s",
          }}
        >
          <ArrowR />
        </span>
      </div>
    </Link>
  );
}

export const ProductCard = memo(ProductCardImpl);
